import {AxiosInstance} from 'axios'
import MyAxiosInstancePriceResearch from './axiosInstancePriceResearch'
import {
    AssistenteOut,
    PriceSearchCreate,
    PriceSearchDetail,
    PriceSearchList,
    PriceSearchOut,
    RevisaoPayload,
} from '../types/priceResearch'

// Service da feature Pesquisa de Preços. Consome o backend price-research em /v1.
// Endpoints (router montado em /v1 com prefixo /pesquisas):
//   POST   /pesquisas            -> 202 PriceSearchOut
//   GET    /pesquisas            -> PriceSearchList
//   GET    /pesquisas/{id}       -> PriceSearchDetail (alvo do polling)
//   DELETE /pesquisas/{id}       -> 204
export class PriceResearchApiService {
    private api: AxiosInstance

    constructor() {
        this.api = MyAxiosInstancePriceResearch
    }

    async createPesquisa(payload: PriceSearchCreate): Promise<PriceSearchOut> {
        const {data} = await this.api.post('/pesquisas', payload)
        return data
    }

    // Saldo de Consultas do usuário (1 Consulta por item). `disponivel: null` = checagem
    // indisponível/desligada — nesse caso o front não deve barrar, só omitir o aviso.
    async getSaldo(): Promise<{disponivel: number | null; max: number | null; current: number | null}> {
        const {data} = await this.api.get('/pesquisas/saldo')
        return data
    }

    // Assistente "✨ preencher com IA" — campo: objeto | detalhar_item | sugerir_itens | fundamentacao_inexigibilidade
    // Endpoint assíncrono: devolve {id, status} de imediato (202); os campos de
    // resultado só vêm preenchidos quando status === 'CONCLUIDO'/'ERRO'. O resultado
    // final chega via notifier (assistenteSocketMessage) ou pelo getAssistente abaixo.
    async assistente(
        campo: string,
        dados: Record<string, any>
    ): Promise<AssistenteOut> {
        const {data} = await this.api.post('/assistente', {campo, dados})
        return data
    }

    // Consulta o status/resultado de um assistente pendente — usado tanto ao reagir
    // ao push do notifier quanto no poll de reconciliação (fallback).
    async getAssistente(id: string): Promise<AssistenteOut> {
        const {data} = await this.api.get(`/assistente/${id}`)
        return data
    }

    // Upload de TR/ETP (PDF/DOCX): a IA extrai os itens e cria a pesquisa.
    async uploadTrEtp(
        file: File,
        periodoMeses: number,
        uf?: string,
        extras?: {processo?: string; orgao?: string}
    ): Promise<PriceSearchOut> {
        const form = new FormData()
        form.append('file', file)
        const {data} = await this.api.post('/pesquisas/upload', form, {
            params: {
                periodo_meses: periodoMeses,
                uf: uf || '',
                processo: extras?.processo || '',
                orgao: extras?.orgao || '',
            },
            headers: {'Content-Type': 'multipart/form-data'},
        })
        return data
    }

    async listPesquisas(
        limit: number = 20,
        offset: number = 0
    ): Promise<PriceSearchList> {
        const {data} = await this.api.get('/pesquisas', {
            params: {limit, offset},
        })
        return data
    }

    async getPesquisa(id: string): Promise<PriceSearchDetail> {
        const {data} = await this.api.get(`/pesquisas/${id}`)
        return data
    }

    // Revisão da cesta: aplica escolhas do usuário e recalcula; retorna o detalhe atualizado.
    async revisar(id: string, payload: RevisaoPayload): Promise<PriceSearchDetail> {
        const {data} = await this.api.post(`/pesquisas/${id}/revisar`, payload)
        return data
    }

    async deletePesquisa(id: string): Promise<void> {
        await this.api.delete(`/pesquisas/${id}`)
    }

    async arquivarPesquisa(id: string): Promise<void> {
        try {
            await this.api.post(`/pesquisas/${id}/arquivar`)
        } catch (error) {
            console.error('Erro ao arquivar pesquisa:', error)
            throw error
        }
    }

    async desarquivarPesquisa(id: string): Promise<void> {
        try {
            await this.api.post(`/pesquisas/${id}/desarquivar`)
        } catch (error) {
            console.error('Erro ao desarquivar pesquisa:', error)
            throw error
        }
    }

    async listArquivadas(): Promise<PriceSearchList> {
        try {
            const {data} = await this.api.get('/pesquisas/arquivadas')
            return data
        } catch (error) {
            console.error('Erro ao buscar pesquisas arquivadas:', error)
            throw error
        }
    }

    // Abre o relatório HTML (com botão "Salvar em PDF") em nova aba.
    async verRelatorio(id: string): Promise<void> {
        const {data} = await this.api.get(`/pesquisas/${id}/relatorio.html`, {
            responseType: 'blob',
        })
        const url = window.URL.createObjectURL(
            new Blob([data], {type: 'text/html'})
        )
        window.open(url, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    }

    // Baixa o relatório no formato pedido (pdf | xlsx | docx | json).
    async exportar(id: string, formato: 'pdf' | 'xlsx' | 'docx' | 'json'): Promise<void> {
        const {data} = await this.api.get(`/pesquisas/${id}/export`, {
            params: {formato},
            responseType: 'blob',
        })
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `pesquisa-precos.${formato}`)
        document.body.appendChild(link)
        link.click()
        link.parentNode?.removeChild(link)
        window.URL.revokeObjectURL(url)
    }

    // Abre a Solicitação de Cotação (Parâmetro IV) em PDF, em nova aba.
    async solicitacaoCotacao(id: string): Promise<void> {
        const {data} = await this.api.get(`/pesquisas/${id}/solicitacao-cotacao`, {
            responseType: 'blob',
        })
        const url = window.URL.createObjectURL(new Blob([data], {type: 'application/pdf'}))
        window.open(url, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    }
}
